import { SelectOption } from './device-shipment.type';

/**
 * Whether a device is part of the lab's active inventory, and why it isn't.
 *
 * Deliberately separate from `device.status`: status is the operational stage
 * (Available / Charging / In Use / …) and is driven by the inspection and test
 * flows, so a deactivated device keeps the stage it was in and gets it back
 * untouched when it is reactivated. An inactive device stays on the Devices
 * screen — greyed out, with the reason on hover — but is never offered for a
 * new test.
 */
export enum DeviceInactiveReason {
  NotWorking = 'NOT_WORKING',
  UnderMaintenance = 'UNDER_MAINTENANCE',
  LostUnavailable = 'LOST_UNAVAILABLE',
  Retired = 'RETIRED',
  Other = 'OTHER',
}

/** The reasons offered in the Deactivate dialog, in display order. */
export const DEVICE_INACTIVE_REASON_OPTIONS: SelectOption[] = [
  { value: DeviceInactiveReason.NotWorking, label: 'Device not working' },
  { value: DeviceInactiveReason.UnderMaintenance, label: 'Under maintenance' },
  { value: DeviceInactiveReason.LostUnavailable, label: 'Lost / unavailable' },
  { value: DeviceInactiveReason.Retired, label: 'Retired' },
  { value: DeviceInactiveReason.Other, label: 'Other' },
];

/**
 * The one reason that takes a free-text note. The named reasons say everything
 * they need to on their own, and the API drops a note sent with any of them.
 */
export const REASON_ACCEPTING_NOTE = DeviceInactiveReason.Other;

/** Matches the column width on the API. */
export const DEVICE_INACTIVE_NOTE_MAX_LENGTH = 500;

/** Wording for a stored reason (blank when the device was deactivated without one). */
export function inactiveReasonLabel(value?: string | null): string {
  if (!value) return '';
  return (
    DEVICE_INACTIVE_REASON_OPTIONS.find((o) => o.value === value)?.label ?? value
  );
}

/** Who opened or resolved an issue — only their name columns are sent. */
export interface DeviceIssueActor {
  id?: number;
  firstName?: string;
  lastName?: string;
}

/**
 * Something that happened to a device and had to be recorded — today always a
 * spell out of service (it was deactivated, why, by whom, and when it came
 * back). `outOfService` is what says so, so an issue that is worth a note but
 * does not pull the device can be logged here later without changing this shape.
 *
 * Kept out of the device row on the API side, the same way shipping legs are: a
 * device has many issues over its lifetime, so each one is its own record and the
 * previous one stays as history. The device carries only the `isActive` flag; the
 * issue currently keeping it out of service hangs off `device.openIssue` (`null`
 * while the device is active), and the full trail is read from
 * GET /devices/:id/issues.
 */
export interface DeviceIssue {
  id?: number;
  deviceId?: number;
  /** Whether this issue takes the device out of the active inventory. */
  outOfService?: boolean;
  reason?: DeviceInactiveReason | string | null;
  note?: string | null;
  openedAt?: string | null;
  openedBy?: DeviceIssueActor | null;
  /** Null while the issue is still open. */
  resolvedAt?: string | null;
  resolvedBy?: DeviceIssueActor | null;

  [extra: string]: any;
}

/** An issue is open until it is resolved; that is what makes it the current one. */
export function isIssueOpen(issue?: DeviceIssue | null): boolean {
  return !!issue && !issue.resolvedAt;
}

/** "Dana Levi", or blank when the acting user is no longer on the account. */
export function issueActorName(actor?: DeviceIssueActor | null): string {
  return [actor?.firstName, actor?.lastName].filter(Boolean).join(' ');
}

/** What the Deactivate dialog sends. Both fields optional. */
export interface DeviceDeactivatePayload {
  reason?: DeviceInactiveReason | null;
  note?: string | null;
}

/** The label the Status cell shows in place of the stage for an inactive device. */
export const INACTIVE_STATUS_LABEL = 'Inactive';

/**
 * The Active / Inactive filter offered in the Devices filter drawer. Sent to the
 * API as `isActive`; an empty value is "no narrowing", which is the default and
 * the only way to see the whole inventory at once.
 */
export type DeviceActiveFilter = '' | 'true' | 'false';

export const DEVICE_ACTIVE_FILTER_OPTIONS: {
  value: DeviceActiveFilter;
  viewValue: string;
}[] = [
  { value: 'true', viewValue: 'Active' },
  { value: 'false', viewValue: 'Inactive' },
];
